/**
 * A agenda vista de longe: a semana e o mês.
 *
 * Aqui o barbeiro não lê nomes — ele lê **espaço**. "Quinta está com 20%", "na
 * semana que vem a sexta está vazia", "o dia 14 é folga". Desenhar cada
 * atendimento numa grade de sete colunas em tela de 390px seria ilegível e não
 * responderia nenhuma dessas perguntas.
 *
 * Tocar num dia leva para a linha do tempo dele, que é onde os nomes estão.
 */

import type { DayLoadView } from '@barbearia/shared';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '../../components/ui.tsx';
import { api } from '../../lib/api.ts';
import { addDays, duration } from '../../lib/format.ts';

export type AgendaScope = 'dia' | 'semana' | 'mes';

interface Props {
  scope: 'semana' | 'mes';
  /** Data em foco — define a semana ou o mês mostrado. */
  date: string;
  today: string;
  membershipId: string | null;
  timezone: string;
  onPickDay: (date: string) => void;
}

/** Segunda-feira da semana da data. */
function startOfWeek(date: string): string {
  const weekday = new Date(`${date}T12:00:00Z`).getUTCDay();
  // Domingo (0) pertence à semana que começou na segunda anterior.
  return addDays(date, weekday === 0 ? -6 : 1 - weekday);
}

function startOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

function daysInMonth(date: string): number {
  const [year, month] = date.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function AgendaOverview({ scope, date, today, membershipId, timezone, onPickDay }: Props) {
  const from = scope === 'semana' ? startOfWeek(date) : startOfMonth(date);
  const total = scope === 'semana' ? 7 : daysInMonth(date);
  const to = addDays(from, total - 1);

  const { data, isPending } = useQuery({
    queryKey: ['overview', from, to, membershipId],
    queryFn: () =>
      api.get<{ days: DayLoadView[] }>('/appointments/overview', {
        from,
        to,
        membershipId: membershipId ?? undefined,
      }),
  });

  if (isPending) return <Spinner />;

  const days = data?.days ?? [];

  return scope === 'semana' ? (
    <WeekView days={days} today={today} timezone={timezone} onPickDay={onPickDay} />
  ) : (
    <MonthView days={days} today={today} from={from} onPickDay={onPickDay} />
  );
}

// ── Semana ──────────────────────────────────────────────────────────────────

const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function weekdayOf(date: string): number {
  return new Date(`${date}T12:00:00Z`).getUTCDay();
}

/**
 * A semana como sete linhas, cada uma com sua barra de ocupação.
 *
 * Linhas empilhadas em vez de colunas lado a lado: numa tela de celular, sete
 * colunas dão 50px cada, o que não cabe nem o dia da semana.
 */
function WeekView({
  days,
  today,
  timezone,
  onPickDay,
}: {
  days: DayLoadView[];
  today: string;
  timezone: string;
  onPickDay: (date: string) => void;
}) {
  const totalFree = days.reduce((sum, day) => sum + day.freeMinutes, 0);
  const totalBooked = days.reduce((sum, day) => sum + day.bookedMinutes, 0);

  return (
    <div className="flex flex-col gap-2 px-4 pt-3">
      <p className="text-[13px] text-ink-soft">
        {totalBooked > 0 ? duration(totalBooked) : 'nada'} marcado ·{' '}
        <span className="font-medium text-ink">{duration(totalFree)} livres</span> na semana
      </p>

      {days.map((day) => (
        <button
          key={day.date}
          type="button"
          onClick={() => onPickDay(day.date)}
          className={`rounded-xl border px-4 py-3 text-left ${
            day.date === today ? 'border-primary bg-primary/5' : 'border-line bg-surface-raised'
          }`}
        >
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[15px] font-medium text-ink">
              {WEEKDAY_SHORT[weekdayOf(day.date)]}{' '}
              <span className="tabular-nums text-ink-soft">
                {day.date.slice(8)}/{day.date.slice(5, 7)}
              </span>
              {day.date === today && <span className="pl-2 text-[12px] text-primary">hoje</span>}
            </span>

            <span className="shrink-0 text-[13px] text-ink-soft">
              {day.closed
                ? 'fechado'
                : day.appointments === 0
                  ? 'dia livre'
                  : `${day.appointments} ${day.appointments === 1 ? 'atendimento' : 'atendimentos'}`}
            </span>
          </div>

          {!day.closed && (
            <>
              <OccupancyBar occupancy={day.occupancy} />
              <div className="flex items-center justify-between pt-1 text-[12px]">
                <span className="text-ink-faint">
                  {day.firstStartsAt && day.lastEndsAt
                    ? `${hhmm(day.firstStartsAt, timezone)} – ${hhmm(day.lastEndsAt, timezone)}`
                    : 'sem nada marcado'}
                </span>
                <span className={day.freeMinutes > 0 ? 'text-ink-soft' : 'text-warn'}>
                  {day.freeMinutes > 0 ? `${duration(day.freeMinutes)} livres` : 'lotado'}
                </span>
              </div>
            </>
          )}

          {day.pending > 0 && (
            <p className="pt-1 text-[12px] font-semibold text-warn">
              {day.pending} {day.pending === 1 ? 'pedido aguardando' : 'pedidos aguardando'}
            </p>
          )}
        </button>
      ))}
    </div>
  );
}

function hhmm(instant: string, timezone: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(instant));
}

function OccupancyBar({ occupancy }: { occupancy: number }) {
  return (
    <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface-sunken">
      <div
        className={`h-full rounded-full ${occupancy >= 0.9 ? 'bg-warn' : 'bg-primary'}`}
        style={{ width: `${Math.max(occupancy * 100, occupancy > 0 ? 4 : 0)}%` }}
      />
    </div>
  );
}

// ── Mês ─────────────────────────────────────────────────────────────────────

/**
 * O mês como calendário, com a ocupação virando intensidade de cor.
 *
 * O número no dia é a contagem de atendimentos; o fundo diz o quanto está
 * cheio. É a visão que responde "quando eu consigo tirar uma tarde".
 */
function MonthView({
  days,
  today,
  from,
  onPickDay,
}: {
  days: DayLoadView[];
  today: string;
  from: string;
  onPickDay: (date: string) => void;
}) {
  // Domingo = 0; a grade começa na segunda, como a semana brasileira de trabalho.
  const firstWeekday = weekdayOf(from);
  const padding = firstWeekday === 0 ? 6 : firstWeekday - 1;

  const lotados = days.filter((day) => !day.closed && day.freeMinutes === 0).length;
  const livres = days.filter((day) => !day.closed && day.appointments === 0).length;

  return (
    <div className="px-4 pt-3">
      <p className="pb-3 text-[13px] text-ink-soft">
        {lotados > 0 && (
          <>
            <span className="font-medium text-ink">{lotados}</span>{' '}
            {lotados === 1 ? 'dia lotado' : 'dias lotados'}
            {livres > 0 && ' · '}
          </>
        )}
        {livres > 0 && (
          <>
            <span className="font-medium text-ink">{livres}</span>{' '}
            {livres === 1 ? 'dia sem nada' : 'dias sem nada'}
          </>
        )}
        {lotados === 0 && livres === 0 && 'Mês com movimento parelho'}
      </p>

      <div className="grid grid-cols-7 gap-1">
        {['S', 'T', 'Q', 'Q', 'S', 'S', 'D'].map((letra, index) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: são posições fixas da grade, não dados
            key={`${letra}-${index}`}
            className="pb-1 text-center text-[11px] font-semibold text-ink-faint"
          >
            {letra}
          </div>
        ))}

        {Array.from({ length: padding }, (_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: espaçadores sem identidade
          <div key={`vazio-${index}`} />
        ))}

        {days.map((day) => (
          <button
            key={day.date}
            type="button"
            onClick={() => onPickDay(day.date)}
            className={`flex touch-target flex-col items-center justify-center rounded-lg border py-1.5 ${
              day.date === today
                ? 'border-primary'
                : day.closed
                  ? 'border-transparent bg-surface-sunken'
                  : 'border-line'
            }`}
            style={
              day.closed
                ? undefined
                : {
                    // A cor da barbearia com opacidade proporcional: um dia
                    // cheio fica sólido, um vazio quase sumido.
                    backgroundColor: `color-mix(in srgb, var(--color-primary) ${Math.round(
                      day.occupancy * 70,
                    )}%, transparent)`,
                  }
            }
          >
            <span
              className={`text-[14px] leading-none font-semibold tabular-nums ${
                day.closed ? 'text-ink-faint' : 'text-ink'
              }`}
            >
              {Number(day.date.slice(8))}
            </span>
            <span className="pt-0.5 text-[10px] leading-none text-ink-soft">
              {day.closed ? '—' : day.appointments > 0 ? day.appointments : ''}
            </span>
            {day.pending > 0 && <span className="pt-0.5 text-[9px] leading-none text-warn">●</span>}
          </button>
        ))}
      </div>

      <p className="pt-3 text-[12px] text-ink-faint">
        A cor mostra o quanto o dia está tomado; o número, quantos atendimentos. Toque para abrir.
      </p>
    </div>
  );
}
